import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import type {
  EmployeeListResponse,
  TCEmployeeVersionGetResponse,
} from 'src/api/generated';
import type { ApiResponse } from 'hooks/api/api';
import { useMemoizedArrayData } from 'hooks/api/api';
import {
  AutomationsEmployeesService,
  EmployeeDraftVersion,
} from 'src/api/generated';

interface EmployeesResponse extends ApiResponse {
  /** Loaded AI Agents in API order. */
  employees: EmployeeListResponse['items'];
  /** Loads the next cursor page. */
  fetchNextPage: () => void;
  /** Whether another cursor page exists. */
  hasNextPage: boolean;
  /** Whether the next cursor page is loading. */
  isFetchingNextPage: boolean;
}

interface EmployeesQueryResponse extends EmployeesResponse {
  /** Number of employee-directory pages loaded by the query. */
  loadedPageCount: number;
  /** Whether the employee directory repeated a requested cursor. */
  repeatedCursor: boolean;
}

interface AllEmployeesResponse extends EmployeesResponse {
  /** Whether the directory stopped before all employees were loaded. */
  directoryIncomplete: boolean;
}

const DEFAULT_COMPLETE_DIRECTORY_MAX_PAGES = 10;

export const useEmployees = (
  limit?: number,
  enabled = true
): EmployeesQueryResponse => {
  const fetchEmployees = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<EmployeeListResponse> => {
    return await AutomationsEmployeesService.list(limit, pageParam);
  };

  const getNextPageParam = (
    lastPage: EmployeeListResponse,
    _allPages: EmployeeListResponse[],
    _lastPageParam: string | undefined,
    allPageParams: (string | undefined)[]
  ) => {
    const nextPageToken = lastPage.nextPageToken;

    return nextPageToken && !allPageParams.includes(nextPageToken)
      ? nextPageToken
      : undefined;
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['automations/employees', { limit }],
    queryFn: fetchEmployees,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const employees = useMemoizedArrayData(
    data?.pages.flatMap((page) => page.items) ?? []
  );
  const loadedPageCount = data?.pages.length ?? 0;
  const nextPageToken = data?.pages.at(-1)?.nextPageToken;
  const pageParams = data?.pageParams ?? [];
  const repeatedCursor = Boolean(
    nextPageToken && pageParams.includes(nextPageToken)
  );
  const loadNextPage = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return {
    loading,
    error,
    employees,
    fetchNextPage: loadNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadedPageCount,
    repeatedCursor,
    refetch,
  };
};

/**
 * Fetches every employee page for views that require a complete tenant directory.
 *
 * @param limit - Maximum number of employees requested per page.
 * @param enabled - Whether the employee query should run.
 * @param maxPages - Maximum cursor pages loaded automatically.
 * @returns The bounded employee collection and query state.
 */
export const useAllEmployees = (
  limit = 100,
  enabled = true,
  maxPages = DEFAULT_COMPLETE_DIRECTORY_MAX_PAGES
): AllEmployeesResponse => {
  const response = useEmployees(limit, enabled);
  const {
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadedPageCount,
    loading,
    repeatedCursor,
  } = response;
  const pageLimit = Math.max(1, maxPages);
  const reachedPageLimit = loadedPageCount >= pageLimit && hasNextPage;
  const directoryIncomplete = repeatedCursor || reachedPageLimit;

  useEffect(() => {
    if (
      !enabled ||
      error ||
      !hasNextPage ||
      isFetchingNextPage ||
      directoryIncomplete
    ) {
      return;
    }

    fetchNextPage();
  }, [
    directoryIncomplete,
    enabled,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  ]);

  return {
    ...response,
    loading:
      loading ||
      (!error && !directoryIncomplete && (hasNextPage || isFetchingNextPage)),
    directoryIncomplete,
  };
};

/**
 * Hook to fetch an employee's metadata by ID
 *
 * TODO: we should implement a hook to only fetch 1 employee, instead of fetching all employees and filtering in the component
 *
 * @param employeeId The ID of the employee to fetch
 * @param enabled Whether the query should be enabled, defaults to true
 * @returns The employee metadata
 */
interface EmployeeMetadataResponse extends ApiResponse {
  employee: EmployeeListResponse['items'][number] | null;
}

export const useEmployeeMetadata = (
  employeeId: string,
  enabled = true
): EmployeeMetadataResponse => {
  const { employees, loading, error, refetch } = useEmployees(
    undefined,
    enabled
  );

  const employee =
    employeeId && enabled
      ? employees.find(
          (emp: EmployeeListResponse['items'][number]) =>
            emp.employeeId === employeeId
        ) ?? null
      : null;

  return {
    loading: enabled ? loading : false,
    error: enabled ? error : null,
    employee,
    refetch,
  };
};

interface EmployeeVersionResponse extends ApiResponse {
  employeeVersion: TCEmployeeVersionGetResponse | null;
}

/**
 * Hook to fetch an employee version
 *
 * @param employeeId The ID of the employee to fetch
 * @param version The version to fetch, defaults to 'DRAFT'
 * @param enabled Whether the query should be enabled, defaults to true
 * @returns The employee version data
 */
export const useEmployeeVersion = (
  employeeId: string,
  version: string | number = EmployeeDraftVersion.DRAFT,
  enabled = true
): EmployeeVersionResponse => {
  const fetchEmployeeVersion =
    async (): Promise<TCEmployeeVersionGetResponse> => {
      return await AutomationsEmployeesService.getVersion(employeeId, version);
    };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['automations/employees/versions', employeeId, version],
    queryFn: fetchEmployeeVersion,
    enabled: enabled,
  });

  return {
    loading,
    error,
    employeeVersion: data ?? null,
    refetch,
  };
};
